import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Download } from "lucide-react";
import { api } from "../lib/api";
import { LightningBolt } from "../components/BrandAccents";

export default function OrderConfirmation() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/orders/${id}`).then(({ data }) => setOrder(data)).catch(() => setOrder(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="max-w-3xl mx-auto px-6 py-24 text-center text-[#8a8076]">Loading…</div>;
  if (!order) return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center">
      <h1 className="font-display text-4xl font-black text-[#1c1815]">Order Not Found</h1>
      <Link to="/" className="mt-4 inline-block text-[#2eb6a8]">← Back home</Link>
    </div>
  );

  const digitals = order.items.filter((i) => i.is_digital);

  return (
    <div data-testid="order-page" className="max-w-3xl mx-auto px-6 py-12 sm:py-16">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#2eb6a8]/12 border-2 border-[#2eb6a8]/40">
          <CheckCircle2 className="text-[#2eb6a8]" size={40}/>
        </div>
        <div className="mt-5 font-script text-3xl text-[#e8478e]">Yay, Babe!</div>
        <h1 className="font-display text-4xl sm:text-6xl font-black text-[#1c1815] tracking-tight">Order Confirmed</h1>
        <p className="mt-3 text-[#4a4239]">Order ID <span className="font-mono text-[#1c1815]">{order.id.slice(0, 8)}</span> — A receipt has been sent to <span className="text-[#2eb6a8] font-bold">{order.email}</span>.</p>
      </div>

      {digitals.length > 0 && (
        <div className="mt-10 p-7 sm:p-9 rounded-3xl border border-[#2eb6a8]/40 bg-gradient-to-br from-[#2eb6a8]/10 to-transparent shadow-sm" data-testid="digital-downloads">
          <div className="flex items-center gap-2">
            <LightningBolt size={20}/>
            <h2 className="font-display font-extrabold text-lg text-[#1c1815]">Your Instant Downloads</h2>
          </div>
          <p className="text-sm text-[#4a4239] mt-1">Click to download — links never expire.</p>
          <div className="mt-5 space-y-3">
            {digitals.map((it) => (
              <a key={it.product_id} href={it.download_url || "#"} target="_blank" rel="noreferrer"
                data-testid={`download-${it.product_id}`}
                className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-[#e5dccd] hover:border-[#2eb6a8] hover:shadow-md transition-all group">
                <img src={it.image_url} alt="" className="w-14 h-14 object-cover rounded-xl"/>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm text-[#1c1815] line-clamp-1">{it.title}</div>
                  <div className="text-xs text-[#8a8076]">Qty {it.quantity}</div>
                </div>
                <span className="inline-flex items-center gap-1.5 bg-[#2eb6a8] group-hover:bg-[#1f8d83] text-white font-bold uppercase tracking-[0.18em] text-[10px] px-4 py-2 rounded-full">
                  <Download size={13}/> Download
                </span>
              </a>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 p-6 rounded-3xl border border-[#e5dccd] bg-white shadow-sm">
        <h3 className="font-display font-extrabold text-lg text-[#1c1815] mb-4">Order Items</h3>
        <div className="space-y-3">
          {order.items.map((it) => (
            <div key={it.product_id} className="flex gap-3 items-center">
              <img src={it.image_url} alt="" className="w-12 h-12 object-cover rounded-xl"/>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-[#1c1815] line-clamp-1">{it.title}</div>
                <div className="text-xs text-[#8a8076]">
                  {it.variant_size && <span>Size {it.variant_size}</span>}
                  {it.variant_size && it.color && <span className="mx-1">·</span>}
                  {it.color && <span>{it.color}</span>}
                  {(it.variant_size || it.color) && <span className="mx-1">·</span>}
                  ${it.price.toFixed(2)} × {it.quantity}
                </div>
              </div>
              <div className="text-sm font-bold text-[#1c1815]">${(it.price * it.quantity).toFixed(2)}</div>
            </div>
          ))}
        </div>
        <div className="mt-5 pt-5 border-t border-[#e5dccd] space-y-1.5 text-sm">
          {order.subtotal !== undefined && (
            <div className="flex justify-between text-[#4a4239]"><span>Subtotal</span><span>${order.subtotal.toFixed(2)}</span></div>
          )}
          {order.discount > 0 && (
            <div className="flex justify-between text-[#c93875]"><span>Discount{order.promo_code ? ` (${order.promo_code})` : ""}</span><span>−${order.discount.toFixed(2)}</span></div>
          )}
          {order.shipping > 0 && (
            <div className="flex justify-between text-[#4a4239]"><span>Shipping</span><span>${order.shipping.toFixed(2)}</span></div>
          )}
          {order.rush_fee > 0 && (
            <div className="flex justify-between text-[#c93875]"><span>{order.rush_label || "Rush delivery"}</span><span>+${order.rush_fee.toFixed(2)}</span></div>
          )}
          {order.tax > 0 && (
            <div className="flex justify-between text-[#4a4239]"><span>{order.tax_label || "Sales tax"}</span><span>${order.tax.toFixed(2)}</span></div>
          )}
          <div className="flex justify-between pt-2 mt-1 border-t border-[#e5dccd]">
            <span className="font-display font-extrabold text-[#1c1815]">Total Paid</span>
            <span data-testid="order-total" className="font-display font-extrabold text-2xl text-[#1c1815]">${order.total.toFixed(2)}</span>
          </div>
        </div>
      </div>

      <div className="mt-8 text-center">
        <Link to="/shop" className="bg-[#e8478e] hover:bg-[#c93875] hover:shadow-lg hover:-translate-y-0.5 text-white font-bold uppercase tracking-[0.2em] text-xs px-7 py-4 rounded-full inline-block transition-all">
          Keep Shopping
        </Link>
      </div>
    </div>
  );
}
